import { getApiEndpoint } from '@/config/api';
import {
  ApiRequestError,
  apiJson,
  apiJsonArray,
  authBearerOnly,
} from '@/lib/api-client';

export interface Partner {
  id: number;
  name: string;
  logo: string;
  address: string;
  website: string;
  email: string;
  phone: string;
  description: string;
  social_media?: string;
  service_type?: string;
  is_active: boolean;
  status?: 'pendente' | 'aprovado' | 'rejeitado';
  created_at: string;
  updated_at: string;
  has_active_vouchers?: boolean | number;
}

export class PartnerService {
  static async getAllPartners(): Promise<Partner[]> {
    return apiJsonArray<Partner>('api/partners');
  }

  static async getAllPartnersForAdmin(): Promise<Partner[]> {
    return apiJsonArray<Partner>('api/partners/admin');
  }

  static async getPartnerById(id: number): Promise<Partner | null> {
    try {
      return await apiJson<Partner>(`api/partners/${id}`);
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  static async getPartnerByIdForAdmin(id: number): Promise<Partner | null> {
    try {
      return await apiJson<Partner>(`api/partners/admin/${id}`);
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  static async createPartner(
    partnerData: Omit<Partner, 'id' | 'created_at' | 'updated_at'>,
  ): Promise<number> {
    const result = await apiJson<{ id: number }>('api/partners', {
      method: 'POST',
      body: JSON.stringify(partnerData),
    });
    return result.id;
  }

  static async updatePartner(
    id: number,
    partnerData: Partial<Partner>,
  ): Promise<boolean> {
    await apiJson(`api/partners/${id}`, {
      method: 'PUT',
      body: JSON.stringify(partnerData),
    });
    return true;
  }

  static async deletePartner(id: number): Promise<boolean> {
    await apiJson(`api/partners/${id}`, { method: 'DELETE' });
    return true;
  }

  static async togglePartnerStatus(id: number): Promise<boolean> {
    await apiJson(`api/partners/${id}/toggle-status`, { method: 'PATCH' });
    return true;
  }

  static async updatePartnerApproval(
    id: number,
    status: 'aprovado' | 'rejeitado',
  ): Promise<boolean> {
    await apiJson(`api/partners/${id}/approval`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
    return true;
  }

  static async uploadLogo(partnerId: number, logoFile: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', logoFile);
    formData.append('partnerId', partnerId.toString());

    const response = await fetch(getApiEndpoint('api/upload/partner-logo'), {
      method: 'POST',
      headers: authBearerOnly(),
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} | ${errorText}`);
    }

    const result = (await response.json()) as { path?: string; filename: string };
    const logoPath = result.path || `/parceiros/${result.filename}`;
    await this.updatePartner(partnerId, { logo: logoPath });
    return logoPath;
  }
}
